abstract class DurationSelectHelpers {
  // Can be used to represent hours in the input field.
  // `.` shortcut behaves like a percentage of the hours, so `0.5` will be `0h 30m`.
  private static hourShortcuts: string[] = ["h", ":", "."];

  // All non-digit characters that are allowed to be entered into the input field
  private static allowedNonDigits: string[] = ["h", ":", ".", "m", " "];

  /**
   * Round minutes up to the nearest increment and convert into an object that has the
   * model (string ex: 1h 5m) and the value.
   * @param value
   */
  private static formatFromMinutes(
    duration: number,
    minDuration: number,
    maxDuration: number,
    roundToIncrement: number,
    incrementDirection?: string,
    defaultDuration?: number
  ): { readout: string; value: number } {
    let value: number = duration;
    // Add or subtract roundToIncrement when using arrow up and down keys
    if (
      incrementDirection === "next" &&
      value <= maxDuration - roundToIncrement
    ) {
      value += roundToIncrement;
    } else if (
      incrementDirection === "previous" &&
      value >= minDuration + roundToIncrement
    ) {
      value -= roundToIncrement;
    }

    if (value > maxDuration) {
      value = defaultDuration || minDuration;
    } else if (value % roundToIncrement !== 0) {
      value = Math.ceil(value / roundToIncrement) * roundToIncrement;
    }

    const hours = Math.floor(value / 60);
    const minutes =
      Math.ceil((value % 60) / roundToIncrement) * roundToIncrement;

    return {
      readout: `${hours}h ${minutes}m`,
      value
    };
  }

  /**
   * Removes invalid characters. Acceptable characters are "0-9", ":", ".", "h", "m".
   * It also removes any allowed non-digit characters that appear twice or more.
   * @param readout
   */
  public static stripInvalidCharacters(readout: string): string {
    let newReadout: string = readout
      .toLowerCase()
      // Remove leading word characters (a-zA-Z) and allowed non-digits
      .replace(new RegExp(`^[a-z\\${this.allowedNonDigits.join("\\")}]+`), "")
      // Remove any non-digit except the allowed non-digits
      .replace(
        new RegExp(`[^0-9 \\${this.allowedNonDigits.join("\\")}]`, "g"),
        ""
      );

    // Remove all characters after the letter "m"
    newReadout = newReadout.includes("m")
      ? `${newReadout.split("m")[0]}m`
      : newReadout;

    // Replace two or more occurances of each allowed non digit character
    this.allowedNonDigits.forEach((allowedNonDigit: string) => {
      newReadout = newReadout.replace(
        new RegExp(`\\${allowedNonDigit}{2,}`, "g"),
        allowedNonDigit
      );
    });

    return newReadout;
  }

  /**
   * Checks the readout for hours and minutes. When there are hours, split the hours and minutes
   * then convert the hours to minutes and combine with the minutes. After this, take those
   * combined minutes and run it through the formatFromMinutes function to return and object
   * with the model & value.
   * @param readout
   * @param defaultDuration
   */
  public static formatDuration(
    readout: string,
    minDuration: number,
    maxDuration: number,
    roundToIncrement: number,
    defaultDuration?: number,
    incrementDirection?: string
  ): {
    readout: string;
    value: number;
  } {
    // Remove invalid characters, including spaces and convert model to an array
    const stripSpaceArray: string[] = readout.replace(/\s+/g, "").split("");
    const stripSpaceAndMArray: string[] = stripSpaceArray
      .join("")
      .replace(/m/g, "")
      .split("");

    // Check the value array for hour shortcuts (the remaining non-digits that represent hours)
    const hourShortcut: string | undefined = this.hourShortcuts.find((hs) =>
      stripSpaceAndMArray.includes(hs)
    );

    let durationInMinutes: number = defaultDuration || minDuration;

    /**
     * When the value is blank, use the `defaultDuration` or `minDuration` and ignore parsing
     * hours and minutes from the value.
     */
    if (!stripSpaceArray.length) {
      return this.formatFromMinutes(
        durationInMinutes,
        minDuration,
        maxDuration,
        roundToIncrement,
        incrementDirection,
        defaultDuration
      );
    }

    // Doesn't include hours so format from minutes
    if (!hourShortcut) {
      durationInMinutes = parseInt(stripSpaceAndMArray.join(""), 10);

      return this.formatFromMinutes(
        durationInMinutes,
        minDuration,
        maxDuration,
        roundToIncrement,
        incrementDirection,
        defaultDuration
      );
    }

    // Split the hours from the minutes on splitIndex character
    const modelSplitOnHoursShortcut: string[] = stripSpaceAndMArray
      .join("")
      .split(hourShortcut);

    const hours: number = +modelSplitOnHoursShortcut[0] || 0;
    let minutes: number = +modelSplitOnHoursShortcut[1] || 0;

    if (hourShortcut === ".") {
      minutes = 60 * parseFloat(`0.${minutes}`);
    }

    // Convert hours to minutes and add to minutes for total duration in minutes
    durationInMinutes = hours * 60 + minutes;

    return this.formatFromMinutes(
      durationInMinutes,
      minDuration,
      maxDuration,
      roundToIncrement,
      incrementDirection,
      defaultDuration
    );
  }
}

export default DurationSelectHelpers;
